import { BuildStatus, isPreReviewBuildStatus } from '@cadstrom/cadstrom-shared';

import type { RevisionStatus } from '@cadstrom/cadstrom-shared';

/** The revision facts the restart rule turns on. */
export interface RestartProcessingContext {
	status: RevisionStatus;
	/** Whether the revision's upload was finalized; false while files are still arriving. */
	hasUploadedFiles: boolean;
	/** The status an errored revision failed from; absent when unknown or not errored. */
	erroredFromStatus?: RevisionStatus;
}

/**
 * Whether a revision is stuck in a way a processing restart can rescue: its files
 * landed and validated, but its design workflow never started, died during ingestion,
 * or gave up waiting and timed the revision out.
 * A revision that errored after the review gate is not offered the restart, since
 * re-running ingestion would not address that failure.
 *
 * Mirrors the precondition the staff-only restart mutation enforces, so an offered
 * command is one the server will accept.
 *
 * @param revision - The revision's upload state, status, and error origin.
 * @returns True when the restart should be offered.
 */
export function canRestartProcessing(revision: RestartProcessingContext): boolean {
	if (!revision.hasUploadedFiles) {
		return false;
	}
	if (revision.status === BuildStatus.Processing || revision.status === BuildStatus.TimedOut) {
		return true;
	}
	if (revision.status !== BuildStatus.Error) {
		return false;
	}
	return revision.erroredFromStatus === undefined || isPreReviewBuildStatus(revision.erroredFromStatus);
}
